/**
 * Top App Bar Component
 * 
 * The main navigation bar for the application.
 */

import { Link, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import lightLogo from '../assets/light/Logo_Light mode.svg';
import darkLogo from '../assets/dark/Logo_Dark mode.svg';
import { UserMenu } from './UserMenu';
import { useThemeModeContext } from './ThemeModeContext';

/** ID of the portal target where the SearchBar is rendered when showing results. */
export const APP_BAR_SEARCH_SLOT_ID = 'app-bar-search-slot';

export function TopAppBar() {
  const navigate = useNavigate();
  const { mode } = useThemeModeContext();
  const logo = mode === 'dark' ? darkLogo : lightLogo;

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Navigate with a new key to force HomePage state reset
    navigate('/', { state: { resetKey: Date.now() } });
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{
        bgcolor: 'background.default',
        color: 'primary.main',
        borderBottom: 1,
        borderColor: 'divider',
        // Mobile: sticky so it flows with content (no fixed-height offset needed).
        // Desktop: fixed with a known height.
        position: { xs: 'sticky', sm: 'fixed' },
        paddingY: { xs: 0.5, sm: 1 },
        display: 'flex',
        justifyContent: 'center',
        // Above the pushed topic drawer (clipped below the bar), so the search dropdown isn't covered.
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          minHeight: 'unset !important',
          position: 'relative',
        }}
      >
        {/* Row 1 on mobile: logo centered, account button right. On desktop: inline. */}
        <Box sx={{
          order: 1,
          display: 'flex',
          alignItems: 'center',
          flex: { xs: '1 1 100%', sm: '0 0 auto' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          position: 'relative',
        }}>
          <Link to="/" onClick={handleLogoClick} style={{ display: 'flex', alignItems: 'center'}}>
            <Box
              component="img"
              src={logo}
              alt="Enterprise Search"
              sx={{
                height: { xs: 48, sm: 56 },
                ml: { xs: 0, sm: 0 },
                mr: { xs: 0, sm: 3 },
              }}
            />
          </Link>
          {/* Account button — on mobile: inside row 1, absolutely positioned right; on desktop: hidden here (shown below) */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <UserMenu />
          </Box>
        </Box>

        {/* Portal slot for the search bar (filled by HomePage when showing results).
            On mobile it drops to row 2 (full-width); on desktop it sits inline. */}
        <Box
          id={APP_BAR_SEARCH_SLOT_ID}
          sx={{
            order: { xs: 5, sm: 2 },
            flex: { xs: '1 1 100%', sm: 1 },
            maxWidth: { sm: 720 },
            '&:not(:empty)': {
              mt: { xs: 1, sm: 0 },
              pb: { xs: 1, sm: 0 },
            },
          }}
        />

        {/* Action buttons — desktop only; on mobile the button is inside the logo row above */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            ml: 'auto',
            order: 4,
          }}
        >
          <UserMenu />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
